// src/store/modules/Ezer.js

import { fetchSmartAlerts } from '@/services/ezer/EzerSmartSuggestions'

// ── Per-user localStorage helpers for persistent UI state ──
const STORAGE_PREFIX = 'ezer_ui_state'
const SESSION_INIT_PREFIX = 'ezer_session_init'
const MESSAGES_PREFIX = 'ezer_messages'

function userStorageKey(userId) {
  return userId ? `${STORAGE_PREFIX}_${userId}` : STORAGE_PREFIX
}

function loadUiState(userId) {
  try {
    const raw = localStorage.getItem(userStorageKey(userId))
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function saveUiState(userId, patch) {
  try {
    const key = userStorageKey(userId)
    const current = loadUiState(userId) || {}
    localStorage.setItem(key, JSON.stringify({ ...current, ...patch }))
  } catch { /* storage full / disabled — ignore */ }
}

// ── Per-user message persistence ──
function messagesStorageKey(userId) {
  return userId ? `${MESSAGES_PREFIX}_${userId}` : null
}

function loadMessages(userId) {
  const key = messagesStorageKey(userId)
  if (!key) return []
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function saveMessages(userId, messages) {
  const key = messagesStorageKey(userId)
  if (!key) return
  try {
    localStorage.setItem(key, JSON.stringify(messages))
  } catch { /* storage full / disabled — ignore */ }
}

function clearMessages(userId) {
  const key = messagesStorageKey(userId)
  if (key) try { localStorage.removeItem(key) } catch { /* ignore */ }
}

/** Persist current messages array to localStorage for the active user. */
function _persistMessages(state) {
  if (state._currentUserId) {
    saveMessages(state._currentUserId, state.messages)
  }
}

// sessionStorage tracks whether the chatbot has already been initialised
// for this login session (cleared when the tab / browser closes).
function isFreshLogin(userId) {
  if (!userId) return false
  try { return !sessionStorage.getItem(`${SESSION_INIT_PREFIX}_${userId}`) }
  catch { return true }
}

function markSessionInit(userId) {
  if (!userId) return
  try { sessionStorage.setItem(`${SESSION_INIT_PREFIX}_${userId}`, '1') }
  catch { /* ignore */ }
}

const isMobileDevice = typeof window !== 'undefined' && window.innerWidth <= 480

// Start closed — ezerInitForUser will set the correct state after login.
const state = {
  isOpen: false,
  isMinimized: false,
  isLoading: false,
  messages: [],             // { id, role, content, toolCalls?, toolUseId?, isError?, timestamp }
  streamingText: '',        // Accumulates during streaming
  error: null,
  pageContext: 'unknown',   // Current page context ID
  sessionId: null,          // Groups messages in a single chat session for DB logging
  feedbackState: null,      // null | 'pending' | 'satisfied' | 'unsatisfied' | 'admin_requested'
  smartAlerts: [],           // Proactive suggestion alerts
  smartAlertsDismissed: false,
  _currentUserId: null,     // Internal: logged-in user for per-user storage
}

const mutations = {
  EZER_SET_OPEN(state, val) { state.isOpen = val },
  EZER_SET_MINIMIZED(state, val) { state.isMinimized = val },
  EZER_SET_LOADING(state, val) { state.isLoading = val },
  EZER_SET_ERROR(state, val) { state.error = val; state.isLoading = false },
  EZER_SET_PAGE_CONTEXT(state, val) { state.pageContext = val },
  EZER_SET_SESSION_ID(state, val) {
    state.sessionId = val
    if (state._currentUserId) saveUiState(state._currentUserId, { sessionId: val })
  },
  EZER_SET_FEEDBACK_STATE(state, val) { state.feedbackState = val },
  EZER_SET_CURRENT_USER_ID(state, val) { state._currentUserId = val },
  EZER_SET_SMART_ALERTS_DISMISSED(state, val) { state.smartAlertsDismissed = val },

  EZER_SET_MESSAGES(state, messages) { state.messages = messages },

  EZER_ADD_MESSAGE(state, msg) {
    state.messages.push({
      id: Date.now() + '-' + Math.random().toString(36).substr(2, 6),
      timestamp: new Date().toISOString(),
      ...msg,
    })
    _persistMessages(state)
  },

  EZER_APPEND_STREAM(state, text) {
    state.streamingText += text
  },

  EZER_FINALIZE_STREAM(state) {
    if (state.streamingText) {
      state.messages.push({
        id: Date.now() + '-' + Math.random().toString(36).substr(2, 6),
        role: 'assistant',
        content: state.streamingText,
        timestamp: new Date().toISOString(),
      })
      state.streamingText = ''
      _persistMessages(state)
    }
  },

  EZER_ADD_TOOL_CALL(state, { toolName, toolInput, toolUseId }) {
    const last = state.messages[state.messages.length - 1]
    if (last?.role === 'assistant') {
      if (!last.toolCalls) last.toolCalls = []
      last.toolCalls.push({ name: toolName, input: toolInput, id: toolUseId, status: 'running' })
      _persistMessages(state)
    }
  },

  EZER_UPDATE_TOOL_STATUS(state, { toolUseId, status }) {
    for (const msg of state.messages) {
      const tc = msg.toolCalls?.find(t => t.id === toolUseId)
      if (tc) { tc.status = status; break }
    }
    _persistMessages(state)
  },

  EZER_SET_SMART_ALERTS(state, alerts) { state.smartAlerts = alerts || [] },
  EZER_DISMISS_SMART_ALERTS(state) { state.smartAlertsDismissed = true },

  EZER_CLEAR(state) {
    state.messages = []
    state.streamingText = ''
    state.error = null
    state.sessionId = null
    state.feedbackState = null
    if (state._currentUserId) {
      clearMessages(state._currentUserId)
      saveUiState(state._currentUserId, { sessionId: null })
    }
  },
}

const actions = {
  /**
   * Called once when the user's session becomes available.
   * Auto-opens the chatbot on a fresh login; restores persisted state otherwise.
   */
  ezerInitForUser({ commit, state }, userId) {
    if (!userId) return
    if (state._currentUserId === userId) return

    // ── User switch: save outgoing user's messages, then clear state ──
    if (state._currentUserId) {
      saveMessages(state._currentUserId, state.messages)
    }
    commit('EZER_CLEAR')
    commit('EZER_SET_CURRENT_USER_ID', userId)

    // ── Restore incoming user's messages & session ──
    const savedMessages = loadMessages(userId)
    if (savedMessages.length > 0) {
      commit('EZER_SET_MESSAGES', savedMessages)
    }
    const persisted = loadUiState(userId)
    if (persisted?.sessionId) {
      commit('EZER_SET_SESSION_ID', persisted.sessionId)
    }

    if (isFreshLogin(userId)) {
      // Fresh login — auto-open (unless mobile)
      const shouldOpen = !isMobileDevice
      commit('EZER_SET_OPEN', shouldOpen)
      commit('EZER_SET_SMART_ALERTS_DISMISSED', false)
      saveUiState(userId, { isOpen: shouldOpen, alertsDismissed: false })
      markSessionInit(userId)
    } else if (persisted) {
      // Returning within same session — restore persisted state
      commit('EZER_SET_OPEN', !!persisted.isOpen)
      commit('EZER_SET_SMART_ALERTS_DISMISSED', !!persisted.alertsDismissed)
    }
    // else: no persisted state and not fresh login — stays closed (default)
  },

  ezerOpen({ commit, state }) {
    commit('EZER_SET_OPEN', true)
    commit('EZER_SET_MINIMIZED', false)
    saveUiState(state._currentUserId, { isOpen: true })
  },
  ezerClose({ commit, state }) {
    commit('EZER_SET_OPEN', false)
    saveUiState(state._currentUserId, { isOpen: false })
  },
  ezerToggle({ state, dispatch }) {
    state.isOpen ? dispatch('ezerClose') : dispatch('ezerOpen')
  },
  ezerMinimize({ commit }) {
    commit('EZER_SET_MINIMIZED', true)
  },
  ezerRestore({ commit }) {
    commit('EZER_SET_MINIMIZED', false)
  },
  async ezerFetchSmartAlerts({ commit, rootGetters }) {
    try {
      // Pass the whole store-like object so the service can read any getter
      const storeProxy = { getters: rootGetters }
      const alerts = await fetchSmartAlerts(storeProxy)
      commit('EZER_SET_SMART_ALERTS', alerts)
    } catch (e) {
      console.warn('Ezer: smart alerts fetch failed', e)
    }
  },
  ezerDismissSmartAlerts({ commit, state }) {
    commit('EZER_DISMISS_SMART_ALERTS')
    saveUiState(state._currentUserId, { alertsDismissed: true })
  },
}

const getters = {
  ezerIsOpen: s => s.isOpen,
  ezerIsMinimized: s => s.isMinimized,
  ezerIsLoading: s => s.isLoading,
  ezerMessages: s => s.messages,
  ezerStreamingText: s => s.streamingText,
  ezerError: s => s.error,
  ezerPageContext: s => s.pageContext,
  ezerHasMessages: s => s.messages.length > 0,
  ezerSessionId: s => s.sessionId,
  ezerFeedbackState: s => s.feedbackState,
  ezerSmartAlerts: s => s.smartAlerts,
  ezerSmartAlertsDismissed: s => s.smartAlertsDismissed,
}

export default { state, mutations, actions, getters }
